/** Compact inline monitor for one background task — the "park it in a page"
 *  counterpart to TaskDetailsDialog.
 *
 *  State chip + label + duration, the shared progress bar, the error message
 *  when failed, cancel (when the task allows it), an expandable live-log panel,
 *  and an "open" button — a deep link to /bgtasks/:id?tab=logs, or `onOpen`
 *  when the host shows TaskDetailsDialog in place. Polls the dialog's query key at
 *  the dialog's cadence while the task is active, so co-mounted monitors share
 *  one request. `onTerminal` fires ONCE when the card first observes a terminal
 *  state — hosts use it to refetch whatever the task produced.
 */
import CancelIcon from '@mui/icons-material/Cancel';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Alert,
  Box,
  Collapse,
  IconButton,
  Paper,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import * as bgtasksApi from '@/api/bgtasks';
import { isActiveState, type BackgroundTaskDetail } from '@/api/bgtasks';
import { useSnackbar } from '@/components/Snackbar';

import { TaskLogsTab } from './TaskLogsTab';
import { TaskProgressBar, TaskStateChip, formatDuration } from './taskFormat';

const DETAIL_POLL_MS = 5000;

interface Props {
  taskId: string;
  /** Fires once, when the card first observes a terminal state. */
  onTerminal?: (task: BackgroundTaskDetail) => void;
  /** Hide the label row (host already names the task). */
  dense?: boolean;
  /** Open the task details in place (the host renders TaskDetailsDialog)
   *  instead of navigating to the Background Tasks page. */
  onOpen?: () => void;
}

export function TaskProgressCard({ taskId, onTerminal, dense = false, onOpen }: Props) {
  const { show } = useSnackbar();
  const queryClient = useQueryClient();
  const [logsOpen, setLogsOpen] = useState(false);
  const notifiedRef = useRef(false);

  const query = useQuery({
    queryKey: ['bgtask', taskId],
    queryFn: () => bgtasksApi.retrieve(taskId),
    refetchInterval: (q) =>
      q.state.data && isActiveState(q.state.data.state) ? DETAIL_POLL_MS : false,
  });
  const task = query.data;

  const onTerminalRef = useRef(onTerminal);
  onTerminalRef.current = onTerminal;
  useEffect(() => {
    if (!task || notifiedRef.current || isActiveState(task.state)) return;
    notifiedRef.current = true;
    onTerminalRef.current?.(task);
  }, [task]);

  const cancelMut = useMutation({
    mutationFn: () => bgtasksApi.cancel(taskId),
    onSuccess: async (r) => {
      show(r.message, 'info');
      await queryClient.invalidateQueries({ queryKey: ['bgtask', taskId] });
    },
    onError: (e) => show((e as Error).message, 'error'),
  });

  if (query.isLoading) {
    return (
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Skeleton variant="rounded" height={dense ? 24 : 48} />
      </Paper>
    );
  }
  if (query.error || !task) {
    return <Alert severity="error">Couldn’t load task {taskId}.</Alert>;
  }

  const duration = formatDuration(task.duration_seconds);
  const progress = task.progress;

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <TaskStateChip task={task} />
        {!dense && (
          <Typography variant="body2" sx={{ fontWeight: 500, flexGrow: 1 }} noWrap>
            {task.task_label}
          </Typography>
        )}
        {dense && <Box sx={{ flexGrow: 1 }} />}
        {duration !== '—' && (
          <Typography variant="caption" color="text.secondary">
            {duration}
          </Typography>
        )}
        {task.can_cancel && isActiveState(task.state) && (
          <Tooltip title="Cancel task">
            <span>
              <IconButton
                size="small"
                onClick={() => cancelMut.mutate()}
                disabled={cancelMut.isPending}
                aria-label="Cancel task"
              >
                <CancelIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Tooltip title={logsOpen ? 'Hide logs' : 'Show logs'}>
          <IconButton
            size="small"
            onClick={() => setLogsOpen((v) => !v)}
            aria-label={logsOpen ? 'Hide logs' : 'Show logs'}
          >
            {logsOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        {onOpen ? (
          <Tooltip title="Open task details">
            <IconButton size="small" onClick={onOpen} aria-label="Open task details">
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Open in Background Tasks">
            <IconButton
              size="small"
              component={RouterLink}
              to={`/bgtasks/${taskId}?tab=logs`}
              aria-label="Open in Background Tasks"
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {progress && (progress.total > 0 || progress.step) && (
        <Box sx={{ mt: 1 }}>
          {progress.step && (
            <Typography variant="caption" color="text.secondary" display="block" noWrap>
              {progress.step}
            </Typography>
          )}
          <TaskProgressBar progress={progress} />
        </Box>
      )}

      {task.error_message && (
        <Alert severity="error" sx={{ mt: 1, py: 0 }}>
          {task.error_message}
        </Alert>
      )}

      <Collapse in={logsOpen} unmountOnExit>
        <Box sx={{ mt: 1.5 }}>
          <TaskLogsTab task={task} />
        </Box>
      </Collapse>
    </Paper>
  );
}
